import React, { useEffect, useState, Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ArrowRight, Smartphone, Zap, Sparkles, Shield, Star, ChevronRight,
  Check, X, Truck, CreditCard, Lock, Clock, BadgeCheck, TrendingUp, Users
} from "lucide-react";
// Below-the-fold: lazy-load the heavy testimonials marquee (framer-motion animation)
const TestimonialsMarquee = lazy(() => import("@/components/TestimonialsMarquee"));
import { api } from "@/api";

// Multilingual copy — colocated for the audit refactor
const T = {
  fr: {
    hero_title_1: "Plus d'avis Google.",
    hero_title_2: "En un simple tap.",
    hero_sub: "Une carte NFC posée sur votre comptoir. Le client approche son téléphone, votre page Google s'ouvre. Il laisse un avis en 10 secondes — sans app, sans QR code.",
    hero_cta: "Commander ma carte NFC",
    hero_cta_alt: "Voir comment ça marche",
    hero_price: "Dès 49,99 $ CAD · Livraison offerte dès 2 cartes",
    trust_1: "500+ commerces équipés",
    trust_2: "★ 4,9 / 5 avis vérifiés",
    trust_3: "Paiement Stripe sécurisé",
    trust_4: "Livraison 2–7 jours",
    trust_5: "Sans app · iPhone & Android",
    how_kicker: "Comment ça marche",
    how_title: "Trois secondes. Trois étapes.",
    how_sub: "Prêt à l'emploi dès la livraison. Aucune configuration côté commerçant.",
    step1_t: "Le client approche son téléphone",
    step1_d: "Il pose son mobile sur la carte NFC — geste naturel, comme un paiement sans contact.",
    step2_t: "Votre page Google s'ouvre",
    step2_d: "Le lien de votre fiche s'affiche instantanément dans le navigateur, sans installation.",
    step3_t: "L'avis est publié",
    step3_d: "Le client note votre commerce et publie son avis en quelques secondes.",
    proof_kicker: "Preuves",
    proof_title: "L'effet NFC, chiffré",
    stat_1_v: "+312 %",
    stat_1_l: "d'avis Google en moyenne sur 90 jours",
    stat_2_v: "10 s",
    stat_2_l: "temps moyen pour laisser un avis",
    stat_3_v: "94 %",
    stat_3_l: "des commerçants équipés recommandent",
    stat_4_v: "500+",
    stat_4_l: "commerces au Canada et en France",
    compare_kicker: "Avec ou sans NFC",
    compare_title: "Ce que change une carte NFC",
    compare_a_h: "Sans carte NFC",
    compare_a_1: "Le client oublie de laisser un avis",
    compare_a_2: "Il faut taper le nom du commerce, chercher la fiche",
    compare_a_3: "Un QR code fatigué scotché sur le comptoir",
    compare_a_4: "Faible flux d'avis, note qui stagne",
    compare_b_h: "Avec NFC Avis Pro",
    compare_b_1: "Un geste réflexe : approche, ouvre, note",
    compare_b_2: "Zéro friction, zéro app, zéro recherche",
    compare_b_3: "Un objet premium qui valorise le comptoir",
    compare_b_4: "Un flux régulier d'avis authentiques",
    who_kicker: "Pour qui",
    who_title: "Conçu pour chaque commerce",
    who_1: "Restaurants & cafés",
    who_2: "Salons & barbiers",
    who_3: "Artisans & services",
    who_4: "Boutiques & retail",
    who_5: "Cliniques & bien-être",
    who_6: "Hôtels & Airbnb",
    lang_kicker: "Deux langues, un même standard",
    lang_title: "Choisissez la carte adaptée à votre clientèle",
    lang_sub: "Mêmes matériaux, même prix, même format. Seule la langue imprimée change.",
    lang_fr: "Version française",
    lang_fr_sub: "« Nous apprécierions votre avis Google »",
    lang_en: "English version",
    lang_en_sub: "« We would appreciate your Google review »",
    pack_kicker: "Meilleure valeur",
    pack_title: "Lot de 4 — équipez plusieurs points de contact",
    pack_sub: "Plusieurs tables, comptoirs ou emplacements ? Le pack de 4 est le choix évident : prix unitaire réduit, livraison offerte.",
    pack_bullet_1: "Livraison offerte",
    pack_bullet_2: "Prix unitaire le plus bas",
    pack_bullet_3: "Expédition en 48 h",
    pack_cta: "Voir le lot de 4",
    faq_kicker: "On répond aux questions",
    faq_title: "Foire aux questions",
    finalcta_title: "Prêt à transformer vos clients satisfaits en avis Google ?",
    finalcta_sub: "Recevez votre carte prête à l'emploi. Aucune configuration, aucune app à installer.",
    finalcta_btn: "Commander maintenant",
    finalcta_alt: "Voir la boutique",
    reassure_1_t: "Paiement Stripe sécurisé",
    reassure_1_d: "Vos données bancaires ne transitent jamais par nous.",
    reassure_2_t: "Remboursement 30 jours",
    reassure_2_d: "Non satisfait ? Renvoyez la carte, on rembourse.",
    reassure_3_t: "Support humain",
    reassure_3_d: "Une équipe basée au Québec, réponse sous 24 h.",
    reassure_4_t: "Programmation offerte",
    reassure_4_d: "Nous programmons chaque carte avec votre lien avant expédition.",
  },
  en: {
    hero_title_1: "More Google reviews.",
    hero_title_2: "One simple tap.",
    hero_sub: "An NFC card on your counter. The customer holds their phone near it, your Google page opens. They leave a review in 10 seconds — no app, no QR code.",
    hero_cta: "Order my NFC card",
    hero_cta_alt: "See how it works",
    hero_price: "From $49.99 CAD · Free shipping on 2+ cards",
    trust_1: "500+ businesses equipped",
    trust_2: "★ 4.9 / 5 verified reviews",
    trust_3: "Secure Stripe checkout",
    trust_4: "Shipping in 2–7 days",
    trust_5: "No app · iPhone & Android",
    how_kicker: "How it works",
    how_title: "Three seconds. Three steps.",
    how_sub: "Ready to use out of the box. Zero setup on your side.",
    step1_t: "Customer taps their phone",
    step1_d: "They rest their phone on the NFC card — like a contactless payment.",
    step2_t: "Your Google page opens",
    step2_d: "Your review link appears instantly in the browser. No install.",
    step3_t: "Review is published",
    step3_d: "They rate and post their review in seconds.",
    proof_kicker: "Proof",
    proof_title: "The NFC effect, in numbers",
    stat_1_v: "+312%",
    stat_1_l: "Google reviews on average over 90 days",
    stat_2_v: "10 s",
    stat_2_l: "average time to leave a review",
    stat_3_v: "94%",
    stat_3_l: "of equipped merchants recommend it",
    stat_4_v: "500+",
    stat_4_l: "businesses across Canada and France",
    compare_kicker: "With or without NFC",
    compare_title: "What an NFC card changes",
    compare_a_h: "Without NFC",
    compare_a_1: "Customers forget to leave a review",
    compare_a_2: "They must type the business name, hunt for the listing",
    compare_a_3: "A tired QR code taped to the counter",
    compare_a_4: "Weak review flow, rating that stalls",
    compare_b_h: "With NFC Avis Pro",
    compare_b_1: "A reflex gesture: tap, open, rate",
    compare_b_2: "Zero friction, zero app, zero search",
    compare_b_3: "A premium object that dresses up the counter",
    compare_b_4: "A steady flow of authentic reviews",
    who_kicker: "Who it's for",
    who_title: "Built for every business",
    who_1: "Restaurants & cafés",
    who_2: "Salons & barbers",
    who_3: "Artisans & services",
    who_4: "Retail & shops",
    who_5: "Clinics & wellness",
    who_6: "Hotels & Airbnb",
    lang_kicker: "Two languages, one standard",
    lang_title: "Pick the card that fits your customers",
    lang_sub: "Same materials, same price, same size. Only the printed language changes.",
    lang_fr: "French version",
    lang_fr_sub: '"Nous apprécierions votre avis Google"',
    lang_en: "English version",
    lang_en_sub: '"We would appreciate your Google review"',
    pack_kicker: "Best value",
    pack_title: "Pack of 4 — equip multiple touchpoints",
    pack_sub: "Multiple tables, counters or locations? The 4-pack is the obvious choice: lower unit price, free shipping.",
    pack_bullet_1: "Free shipping included",
    pack_bullet_2: "Lowest unit price",
    pack_bullet_3: "Ships within 48h",
    pack_cta: "See the 4-pack",
    faq_kicker: "We answer",
    faq_title: "Frequently asked questions",
    finalcta_title: "Ready to turn happy customers into Google reviews?",
    finalcta_sub: "Receive your card ready to use. No setup, no app to install.",
    finalcta_btn: "Order now",
    finalcta_alt: "See the shop",
    reassure_1_t: "Secure Stripe checkout",
    reassure_1_d: "Your card data never touches our servers.",
    reassure_2_t: "30-day refund",
    reassure_2_d: "Not happy? Send it back, we refund.",
    reassure_3_t: "Human support",
    reassure_3_d: "A Québec-based team, reply within 24h.",
    reassure_4_t: "Programming included",
    reassure_4_d: "We program each card with your link before shipping.",
  },
  es: {
    hero_title_1: "Más reseñas de Google.",
    hero_title_2: "Con un simple toque.",
    hero_sub: "Una tarjeta NFC en tu mostrador. El cliente acerca su móvil, se abre tu página de Google. Deja una reseña en 10 segundos — sin app, sin código QR.",
    hero_cta: "Pedir mi tarjeta NFC",
    hero_cta_alt: "Ver cómo funciona",
    hero_price: "Desde 49,99 $ CAD · Envío gratis desde 2 tarjetas",
    trust_1: "500+ comercios equipados",
    trust_2: "★ 4,9 / 5 reseñas verificadas",
    trust_3: "Pago Stripe seguro",
    trust_4: "Envío en 2–7 días",
    trust_5: "Sin app · iPhone y Android",
    how_kicker: "Cómo funciona",
    how_title: "Tres segundos. Tres pasos.",
    how_sub: "Listo para usar. Cero configuración de tu lado.",
    step1_t: "El cliente acerca su móvil",
    step1_d: "Apoya el teléfono sobre la tarjeta — como un pago sin contacto.",
    step2_t: "Se abre tu página de Google",
    step2_d: "Tu enlace de reseñas aparece al instante en el navegador. Sin instalación.",
    step3_t: "La reseña se publica",
    step3_d: "Puntúa y publica su reseña en pocos segundos.",
    proof_kicker: "Pruebas",
    proof_title: "El efecto NFC en cifras",
    stat_1_v: "+312 %",
    stat_1_l: "reseñas de Google de media en 90 días",
    stat_2_v: "10 s",
    stat_2_l: "tiempo medio para dejar una reseña",
    stat_3_v: "94 %",
    stat_3_l: "de los comercios equipados lo recomiendan",
    stat_4_v: "500+",
    stat_4_l: "negocios en Canadá y Francia",
    compare_kicker: "Con o sin NFC",
    compare_title: "Lo que cambia una tarjeta NFC",
    compare_a_h: "Sin NFC",
    compare_a_1: "El cliente se olvida de dejar una reseña",
    compare_a_2: "Debe escribir el nombre, buscar la ficha",
    compare_a_3: "Un QR cansado pegado al mostrador",
    compare_a_4: "Flujo débil, valoración estancada",
    compare_b_h: "Con NFC Avis Pro",
    compare_b_1: "Un gesto reflejo: acerca, abre, valora",
    compare_b_2: "Cero fricción, cero app, cero búsqueda",
    compare_b_3: "Un objeto premium que decora el mostrador",
    compare_b_4: "Un flujo constante de reseñas auténticas",
    who_kicker: "Para quién",
    who_title: "Hecho para todos los comercios",
    who_1: "Restaurantes y cafés",
    who_2: "Salones y barberías",
    who_3: "Artesanos y servicios",
    who_4: "Tiendas y retail",
    who_5: "Clínicas y bienestar",
    who_6: "Hoteles y Airbnb",
    lang_kicker: "Dos idiomas, un mismo estándar",
    lang_title: "Elige la tarjeta adaptada a tus clientes",
    lang_sub: "Mismos materiales, mismo precio, mismo formato. Solo cambia el idioma impreso.",
    lang_fr: "Versión francesa",
    lang_fr_sub: '"Nous apprécierions votre avis Google"',
    lang_en: "Versión en inglés",
    lang_en_sub: '"We would appreciate your Google review"',
    pack_kicker: "Mejor valor",
    pack_title: "Pack de 4 — equipa varios puntos de contacto",
    pack_sub: "¿Varias mesas, mostradores o locales? El pack de 4 es la mejor opción: precio unitario menor, envío gratis.",
    pack_bullet_1: "Envío gratis incluido",
    pack_bullet_2: "Precio unitario más bajo",
    pack_bullet_3: "Expedición en 48h",
    pack_cta: "Ver el pack de 4",
    faq_kicker: "Respondemos",
    faq_title: "Preguntas frecuentes",
    finalcta_title: "¿Listo para convertir clientes felices en reseñas de Google?",
    finalcta_sub: "Recibe tu tarjeta lista para usar. Sin configuración, sin app.",
    finalcta_btn: "Pedir ahora",
    finalcta_alt: "Ver la tienda",
    reassure_1_t: "Pago Stripe seguro",
    reassure_1_d: "Tus datos bancarios no pasan por nosotros.",
    reassure_2_t: "Reembolso 30 días",
    reassure_2_d: "¿No te convence? Devuélvela y reembolsamos.",
    reassure_3_t: "Soporte humano",
    reassure_3_d: "Equipo en Québec, respuesta en 24h.",
    reassure_4_t: "Programación incluida",
    reassure_4_d: "Programamos cada tarjeta con tu enlace antes de enviar.",
  },
  zh: {
    hero_title_1: "更多 Google 评价。",
    hero_title_2: "轻轻一触。",
    hero_sub: "一张 NFC 卡放在您的柜台上。顾客将手机靠近它,您的 Google 页面即刻打开。10 秒内留下评价 — 无需应用,无需二维码。",
    hero_cta: "订购我的 NFC 卡",
    hero_cta_alt: "了解工作原理",
    hero_price: "49.99 加元起 · 2 张起免运费",
    trust_1: "500+ 商家已装备",
    trust_2: "★ 4.9 / 5 认证评价",
    trust_3: "Stripe 安全支付",
    trust_4: "2–7 天送达",
    trust_5: "无需应用 · iPhone 和 Android",
    how_kicker: "工作原理",
    how_title: "三秒。三步。",
    how_sub: "开箱即用。您无需任何设置。",
    step1_t: "顾客靠近手机",
    step1_d: "将手机贴近 NFC 卡 — 像刷卡支付一样自然。",
    step2_t: "您的 Google 页面打开",
    step2_d: "评价链接即刻显示在浏览器中,无需安装。",
    step3_t: "评价已发布",
    step3_d: "他们评分并在几秒内发布评价。",
    proof_kicker: "数据证明",
    proof_title: "NFC 效果,用数据说话",
    stat_1_v: "+312%",
    stat_1_l: "90 天平均 Google 评价增长",
    stat_2_v: "10 秒",
    stat_2_l: "平均留下评价所需时间",
    stat_3_v: "94%",
    stat_3_l: "已装备商家的推荐率",
    stat_4_v: "500+",
    stat_4_l: "加拿大和法国的商家",
    compare_kicker: "有或没有 NFC",
    compare_title: "一张 NFC 卡带来的改变",
    compare_a_h: "没有 NFC",
    compare_a_1: "顾客忘记留下评价",
    compare_a_2: "必须输入商家名,搜索店铺",
    compare_a_3: "柜台上贴着陈旧的二维码",
    compare_a_4: "评价数量少,评分停滞",
    compare_b_h: "有 NFC Avis Pro",
    compare_b_1: "本能动作:靠近、打开、评分",
    compare_b_2: "零摩擦、无需应用、无需搜索",
    compare_b_3: "为柜台增添质感的高档物件",
    compare_b_4: "稳定的真实评价流入",
    who_kicker: "适合谁",
    who_title: "为每一种商家打造",
    who_1: "餐厅和咖啡馆",
    who_2: "美发和理发店",
    who_3: "工匠和服务业",
    who_4: "零售和门店",
    who_5: "诊所和健康",
    who_6: "酒店和 Airbnb",
    lang_kicker: "两种语言,同一标准",
    lang_title: "选择适合您客户的卡片",
    lang_sub: "相同材质,相同价格,相同规格。仅印刷语言不同。",
    lang_fr: "法语版",
    lang_fr_sub: "《Nous apprécierions votre avis Google》",
    lang_en: "英语版",
    lang_en_sub: "《We would appreciate your Google review》",
    pack_kicker: "最超值",
    pack_title: "4 张套装 — 装备多个接触点",
    pack_sub: "多张餐桌、柜台或门店?4 张套装是显而易见的选择:更低单价,免运费。",
    pack_bullet_1: "含免运费",
    pack_bullet_2: "最低单价",
    pack_bullet_3: "48 小时内发货",
    pack_cta: "查看 4 张套装",
    faq_kicker: "我们的回答",
    faq_title: "常见问题",
    finalcta_title: "准备好把满意的顾客转化为 Google 评价了吗?",
    finalcta_sub: "收到即用。无需设置,无需应用。",
    finalcta_btn: "立即订购",
    finalcta_alt: "查看商店",
    reassure_1_t: "Stripe 安全支付",
    reassure_1_d: "您的支付信息从不经过我们的服务器。",
    reassure_2_t: "30 天退款",
    reassure_2_d: "不满意?寄回即退款。",
    reassure_3_t: "真人客服",
    reassure_3_d: "魁北克团队,24 小时内响应。",
    reassure_4_t: "含编程服务",
    reassure_4_d: "发货前为每张卡片写入您的链接。",
  },
};

const HomePage = () => {
  const { t, lang } = useLang();
  const [products, setProducts] = useState([]);
  const c = T[lang] || T.fr;

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* ============ HERO — Règle des 5 secondes ============ */}
      <section className="relative overflow-hidden hero-glow" data-testid="hero-section">
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top_left,rgba(37,99,235,0.15),transparent_60%)]" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-24 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/80 border border-slate-800 text-blue-400 text-xs font-semibold uppercase tracking-wider" data-testid="hero-badge">
              <Sparkles className="w-3.5 h-3.5" /> {t.hero.badge}
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.02]" data-testid="hero-title">
              {c.hero_title_1}
              <br />
              <span className="text-blue-400">{c.hero_title_2}</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-xl" data-testid="hero-subtitle">
              {c.hero_sub}
            </p>

            {/* Single primary CTA + textual secondary */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] px-8 h-12 text-base font-semibold shadow-lg shadow-blue-600/30" data-testid="hero-cta-shop">
                <Link to="/boutique">{c.hero_cta} <ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
              <Link to="/fonctionnement" className="text-slate-300 hover:text-white text-sm font-medium underline underline-offset-4 decoration-slate-600 hover:decoration-blue-400" data-testid="hero-cta-how">
                {c.hero_cta_alt} →
              </Link>
            </div>

            {/* Price anchor + shipping */}
            <p className="mt-6 text-sm text-slate-400 font-medium" data-testid="hero-price">{c.hero_price}</p>

            {/* Trust pastilles */}
            <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-400" data-testid="hero-trust">
              <span className="inline-flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" /> {c.trust_2}</span>
              <span className="inline-flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-blue-400" /> {c.trust_4}</span>
              <span className="inline-flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5 text-blue-400" /> {c.trust_5}</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="relative">
            <div className="rounded-3xl overflow-hidden bg-white shadow-2xl shadow-blue-500/20 aspect-square max-w-[560px] mx-auto ring-1 ring-slate-800">
              <img src="/products/Image_11.webp" alt="Client utilisant une carte NFC Avis Pro — un tap ouvre la page Google Reviews de l'entreprise" className="w-full h-full object-cover" loading="eager" fetchPriority="high" decoding="async" width="560" height="560" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ TRUST BAR (juste après hero) ============ */}
      <section className="border-y border-slate-900 bg-[#050505]" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs md:text-sm text-slate-400">
          <span className="inline-flex items-center gap-2"><Users className="w-4 h-4 text-blue-400" /> {c.trust_1}</span>
          <span className="hidden md:block h-4 w-px bg-slate-800" />
          <span className="inline-flex items-center gap-2"><Star className="w-4 h-4 text-yellow-400 fill-yellow-400" /> {c.trust_2}</span>
          <span className="hidden md:block h-4 w-px bg-slate-800" />
          <span className="inline-flex items-center gap-2"><Lock className="w-4 h-4 text-blue-400" /> {c.trust_3}</span>
          <span className="hidden md:block h-4 w-px bg-slate-800" />
          <span className="inline-flex items-center gap-2"><Truck className="w-4 h-4 text-blue-400" /> {c.trust_4}</span>
        </div>
      </section>

      {/* ============ COMMENT ÇA MARCHE (Expliquer avant de vendre) ============ */}
      <section className="py-20 md:py-28" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-semibold text-blue-400 uppercase tracking-[0.15em]">{c.how_kicker}</p>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight">{c.how_title}</h2>
            <p className="mt-4 text-slate-400 text-base md:text-lg">{c.how_sub}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 md:gap-6">
            {[
              { icon: Smartphone, t: c.step1_t, d: c.step1_d, n: "01" },
              { icon: Zap, t: c.step2_t, d: c.step2_d, n: "02" },
              { icon: Star, t: c.step3_t, d: c.step3_d, n: "03" },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Card className="p-8 border-slate-800 bg-[#0a0a0a] hover:border-blue-500/30 hover:-translate-y-1 transition-all h-full rounded-2xl">
                  <div className="text-6xl font-bold text-slate-800 -mb-4 tracking-tighter">{s.n}</div>
                  <s.icon className="w-8 h-8 text-blue-400" />
                  <h3 className="mt-4 text-xl font-semibold text-white">{s.t}</h3>
                  <p className="mt-2 text-slate-400 leading-relaxed">{s.d}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PROOF — CHIFFRES ============ */}
      <section className="py-20 md:py-24 bg-[#0a0a0a] border-y border-slate-900" data-testid="proof-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-semibold text-blue-400 uppercase tracking-[0.15em]">{c.proof_kicker}</p>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight">{c.proof_title}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {[
              { v: c.stat_1_v, l: c.stat_1_l, i: TrendingUp },
              { v: c.stat_2_v, l: c.stat_2_l, i: Clock },
              { v: c.stat_3_v, l: c.stat_3_l, i: BadgeCheck },
              { v: c.stat_4_v, l: c.stat_4_l, i: Users },
            ].map((s, i) => (
              <div key={i} className="p-6 md:p-7 rounded-2xl bg-black border border-slate-800 hover:border-blue-500/30 transition-colors">
                <s.i className="w-5 h-5 text-blue-400" />
                <div className="mt-4 text-4xl md:text-5xl font-bold text-white tracking-tight">{s.v}</div>
                <p className="mt-2 text-xs md:text-sm text-slate-400 leading-snug">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS (auto-scrolling marquee) — lazy-loaded, below the fold ============ */}
      <Suspense fallback={<div className="py-20" aria-hidden="true" />}>
        <TestimonialsMarquee />
      </Suspense>

      {/* ============ COMPARAISON AVEC vs SANS NFC ============ */}
      <section className="py-20 md:py-28" data-testid="compare-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-semibold text-blue-400 uppercase tracking-[0.15em]">{c.compare_kicker}</p>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight">{c.compare_title}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5 md:gap-6">
            <div className="p-7 md:p-8 rounded-2xl bg-[#0a0a0a] border border-slate-800">
              <h3 className="text-lg font-semibold text-slate-300">{c.compare_a_h}</h3>
              <ul className="mt-5 space-y-3">
                {[c.compare_a_1, c.compare_a_2, c.compare_a_3, c.compare_a_4].map((x, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-400 text-sm md:text-base">
                    <X className="w-4 h-4 mt-0.5 text-slate-600 flex-shrink-0" />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-7 md:p-8 rounded-2xl bg-gradient-to-br from-blue-600/10 to-black border border-blue-500/40 shadow-xl shadow-blue-500/10">
              <h3 className="text-lg font-semibold text-white inline-flex items-center gap-2">
                <BadgeCheck className="w-5 h-5 text-blue-400" /> {c.compare_b_h}
              </h3>
              <ul className="mt-5 space-y-3">
                {[c.compare_b_1, c.compare_b_2, c.compare_b_3, c.compare_b_4].map((x, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-200 text-sm md:text-base">
                    <Check className="w-4 h-4 mt-0.5 text-blue-400 flex-shrink-0" />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============ POUR QUI (secteurs) ============ */}
      <section className="py-20 md:py-24 bg-[#0a0a0a] border-y border-slate-900" data-testid="who-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-semibold text-blue-400 uppercase tracking-[0.15em]">{c.who_kicker}</p>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight">{c.who_title}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {[c.who_1, c.who_2, c.who_3, c.who_4, c.who_5, c.who_6].map((s, i) => (
              <div key={i} className="px-5 py-4 rounded-xl bg-black border border-slate-800 text-slate-300 text-sm md:text-base font-medium hover:border-blue-500/40 hover:text-white transition-colors">
                {s}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CHOIX DE CARTE FR / EN ============ */}
      <section className="py-20 md:py-28" data-testid="bilingual-showcase">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-semibold text-blue-400 uppercase tracking-[0.15em]">{c.lang_kicker}</p>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight">{c.lang_title}</h2>
            <p className="mt-4 text-slate-400 text-base md:text-lg">{c.lang_sub}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {[
              { code: "fr", title: c.lang_fr, sub: c.lang_fr_sub, img: "/products/Image_01.webp" },
              { code: "en", title: c.lang_en, sub: c.lang_en_sub, img: "/products/Image_02.webp" },
            ].map((cd) => (
              <div key={cd.code} className="group relative rounded-3xl bg-gradient-to-br from-slate-900 to-black border border-slate-800 hover:border-blue-500/40 p-6 md:p-8 transition-all hover:-translate-y-1" data-testid={`showcase-card-${cd.code}`}>
                <div className="flex items-center gap-3 mb-5">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/30 font-bold text-sm uppercase">{cd.code}</span>
                  <div>
                    <div className="text-lg font-semibold text-white">{cd.title}</div>
                    <div className="text-xs text-slate-400 italic">{cd.sub}</div>
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden bg-white flex items-center justify-center p-4 shadow-2xl shadow-blue-500/10 group-hover:shadow-blue-500/20 transition-shadow">
                  <img src={cd.img} alt={`Plaque NFC — ${cd.title}`} className="w-full max-w-[340px] object-contain group-hover:scale-[1.02] transition-transform duration-500" loading="lazy" decoding="async" width="340" height="340" />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Button asChild size="lg" className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] px-8 h-12 font-semibold shadow-lg shadow-blue-600/30" data-testid="showcase-cta">
              <Link to="/boutique">{c.hero_cta} <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ============ PACK 4 — VALUE STACKING ============ */}
      <section className="py-20 md:py-28 bg-[#0a0a0a] border-y border-slate-900" data-testid="pack4-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="rounded-3xl overflow-hidden bg-white shadow-2xl shadow-blue-500/10 ring-1 ring-slate-800">
            <img src="/products/Image_17.webp" alt="Pack de 4 plaques NFC Google — meilleure valeur, livraison offerte" className="w-full h-auto object-contain" loading="lazy" decoding="async" width="800" height="800" />
          </div>
          <div>
            <p className="text-xs font-semibold text-blue-400 uppercase tracking-[0.15em]">{c.pack_kicker}</p>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight">{c.pack_title}</h2>
            <p className="mt-4 text-slate-400 leading-relaxed text-base md:text-lg">{c.pack_sub}</p>
            <ul className="mt-6 space-y-2.5">
              {[c.pack_bullet_1, c.pack_bullet_2, c.pack_bullet_3].map((b, i) => (
                <li key={i} className="flex items-center gap-2.5 text-slate-200 text-sm md:text-base">
                  <Check className="w-4 h-4 text-blue-400" /> {b}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Button asChild size="lg" className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] px-8 h-12 font-semibold shadow-lg shadow-blue-600/30" data-testid="pack4-cta">
                <Link to="/boutique">{c.pack_cta} <ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ (objections) ============ */}
      <section className="py-20 md:py-28" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="mb-10">
            <p className="text-xs font-semibold text-blue-400 uppercase tracking-[0.15em]">{c.faq_kicker}</p>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight">{c.faq_title}</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {t.faq.items.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border border-slate-800 rounded-2xl bg-[#0a0a0a] px-5" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-semibold hover:no-underline text-white">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-400 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ============ CTA FINAL ANCRANT ============ */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-[#0a0a0a] to-black border-t border-slate-900" data-testid="finalcta-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight">
            {c.finalcta_title}
          </h2>
          <p className="mt-5 text-slate-400 text-base md:text-lg max-w-2xl mx-auto">{c.finalcta_sub}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg" className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] px-8 h-12 text-base font-semibold shadow-lg shadow-blue-600/30" data-testid="finalcta-primary">
              <Link to="/boutique">{c.finalcta_btn} <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
            <Link to="/boutique" className="text-slate-300 hover:text-white text-sm font-medium underline underline-offset-4 decoration-slate-600 hover:decoration-blue-400" data-testid="finalcta-alt">
              {c.finalcta_alt} →
            </Link>
          </div>
        </div>
      </section>

      {/* ============ RASSURANCE PREMIUM (remplace le disclaimer) ============ */}
      <section className="pb-20 md:pb-28" data-testid="reassure-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {[
              { i: CreditCard, t: c.reassure_1_t, d: c.reassure_1_d },
              { i: Shield, t: c.reassure_2_t, d: c.reassure_2_d },
              { i: Users, t: c.reassure_3_t, d: c.reassure_3_d },
              { i: BadgeCheck, t: c.reassure_4_t, d: c.reassure_4_d },
            ].map((r, i) => (
              <div key={i} className="p-5 md:p-6 rounded-2xl bg-[#0a0a0a] border border-slate-800">
                <r.i className="w-5 h-5 text-blue-400" />
                <h3 className="mt-3 text-sm md:text-base font-semibold text-white">{r.t}</h3>
                <p className="mt-1.5 text-xs md:text-sm text-slate-400 leading-snug">{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
